import React, { useState } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { 
  ObserveIcon, FeelIcon, NeedIcon, BridgeIcon, 
  SparkleIcon, ArrowIcon, CheckIcon, CloseIcon 
} from './BespokeIcons';

/**
 * NVCCanvasOverlay - OFNR overlay for the SketchHub canvas
 * 
 * Provides a visual overlay showing the four NVC quadrants:
 * - Observations (What I see/hear)
 * - Feelings (How I feel)
 * - Needs (What I need)
 * - Requests (What I'd like)
 */

// OFNR Quadrant definitions
const OFNR_QUADRANTS = {
  observations: {
    key: 'observations',
    label: 'Observations',
    shortLabel: 'O',
    subtitle: 'What I see/hear',
    color: '#3B82F6',
    bgColor: 'rgba(59, 130, 246, 0.08)',
    borderColor: 'rgba(59, 130, 246, 0.3)',
    icon: ObserveIcon,
    position: { top: '0', left: '0', width: '50%', height: '50%' },
    prompt: 'Describe what you observed without evaluation...',
    examples: ['I noticed...', 'When I heard...', 'I saw that...']
  },
  feelings: {
    key: 'feelings',
    label: 'Feelings',
    shortLabel: 'F',
    subtitle: 'How I feel',
    color: '#EF4444',
    bgColor: 'rgba(239, 68, 68, 0.08)',
    borderColor: 'rgba(239, 68, 68, 0.3)',
    icon: FeelIcon,
    position: { top: '0', right: '0', width: '50%', height: '50%' },
    prompt: 'Name the feelings arising in you...',
    examples: ['I feel...', 'I\'m experiencing...', 'This brings up...']
  },
  needs: {
    key: 'needs',
    label: 'Needs',
    shortLabel: 'N',
    subtitle: 'What I need',
    color: '#10B981',
    bgColor: 'rgba(16, 185, 129, 0.08)',
    borderColor: 'rgba(16, 185, 129, 0.3)',
    icon: NeedIcon,
    position: { bottom: '0', left: '0', width: '50%', height: '50%' },
    prompt: 'What universal human needs are alive in you?',
    examples: ['I need...', 'What matters to me is...', 'I value...']
  },
  requests: {
    key: 'requests',
    label: 'Requests',
    shortLabel: 'R',
    subtitle: 'What I\'d like',
    color: '#F59E0B',
    bgColor: 'rgba(245, 158, 11, 0.08)',
    borderColor: 'rgba(245, 158, 11, 0.3)',
    icon: BridgeIcon,
    position: { bottom: '0', right: '0', width: '50%', height: '50%' },
    prompt: 'What specific, doable request would meet your needs?',
    examples: ['Would you be willing to...', 'I\'d like to request...', 'Could we...']
  }
};

// NVC Quadrant Overlay Item
const NVCQuadrant = ({ quadrant, isActive, onClick, hasContent = false }) => {
  const { label, subtitle, color, bgColor, borderColor, icon: Icon, position } = quadrant;
  
  return (
    <div
      onClick={onClick}
      className={`
        absolute transition-all duration-300 cursor-pointer
        flex flex-col items-center justify-center
        ${isActive ? 'z-10' : 'z-0'}
      `}
      style={{
        ...position,
        backgroundColor: isActive ? bgColor : 'transparent',
        borderColor: borderColor,
        borderWidth: '1px',
        borderStyle: 'dashed',
      }}
    >
      {/* Label */}
      <div className={`
        flex flex-col items-center gap-2 p-4 rounded-xl transition-all
        ${isActive ? 'scale-110' : 'opacity-60 hover:opacity-100'}
      `}>
        <div 
          className="w-12 h-12 rounded-full flex items-center justify-center"
          style={{ backgroundColor: `${color}20`, border: `2px solid ${color}40` }}
        >
          <Icon size={24} color={color} />
        </div>
        <div className="text-center">
          <p className="text-sm font-medium" style={{ color }}>{label}</p>
          <p className="text-xs text-white/40">{subtitle}</p>
        </div>
        {hasContent && (
          <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
        )}
      </div>
    </div>
  );
};

// Toggle button to show/hide NVC overlay
export const NVCOverlayToggle = ({ isActive, onToggle, className = '' }) => (
  <Button
    onClick={onToggle}
    variant={isActive ? 'default' : 'outline'}
    size="sm"
    className={`
      ${isActive 
        ? 'bg-gradient-to-r from-blue-500 via-emerald-500 to-amber-500 text-white border-0' 
        : 'border-white/20 text-white/60 hover:text-white hover:border-white/40'
      }
      ${className}
    `}
    title="Toggle NVC Overlay (OFNR)"
  >
    <span className="mr-1 text-xs font-bold">OFNR</span>
    <SparkleIcon size={14} color={isActive ? '#FFFFFF' : '#9CA3AF'} />
  </Button>
);

// Full NVC Canvas Overlay
export const NVCCanvasOverlay = ({ 
  isVisible,
  activeQuadrant,
  onQuadrantClick,
  contentByQuadrant = {} // { observations: [...], feelings: [...], etc }
}) => {
  if (!isVisible) return null;
  
  return (
    <div className="absolute inset-0 pointer-events-none z-20">
      {/* Semi-transparent overlay */}
      <div className="absolute inset-0 bg-black/20" />
      
      {/* Quadrants */}
      <div className="absolute inset-4 pointer-events-auto">
        {Object.values(OFNR_QUADRANTS).map((quadrant) => (
          <NVCQuadrant
            key={quadrant.key}
            quadrant={quadrant}
            isActive={activeQuadrant === quadrant.key}
            onClick={() => onQuadrantClick?.(quadrant.key)}
            hasContent={(contentByQuadrant[quadrant.key] || []).length > 0}
          />
        ))}
        
        {/* Center icon */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-30">
          <div className="w-16 h-16 rounded-full bg-zinc-900/90 border-2 border-amber-500/50 flex items-center justify-center shadow-lg shadow-amber-500/20">
            <SparkleIcon size={28} color="#FBBF24" />
          </div>
        </div>
      </div>
      
      {/* Legend */}
      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-4 bg-zinc-900/80 backdrop-blur-sm px-4 py-2 rounded-full">
        {Object.values(OFNR_QUADRANTS).map(q => (
          <div key={q.key} className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: q.color }} />
            <span className="text-[10px] text-white/60">{q.shortLabel}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// NVC Quick Input Dialog
export const NVCQuickInput = ({
  isOpen,
  onClose,
  quadrant,
  value,
  onChange,
  onSave
}) => {
  const quadrantConfig = OFNR_QUADRANTS[quadrant];
  if (!quadrantConfig) return null;
  
  const { label, subtitle, color, icon: Icon, prompt, examples } = quadrantConfig;
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="bg-zinc-900/95 border-zinc-700/50 max-w-md"
        style={{ borderColor: `${color}40` }}
      >
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div 
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ backgroundColor: `${color}20`, border: `2px solid ${color}40` }}
            >
              <Icon size={20} color={color} />
            </div>
            <div>
              <DialogTitle style={{ color }}>{label}</DialogTitle>
              <p className="text-white/50 text-xs">{subtitle}</p>
            </div>
          </div>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <p className="text-white/60 text-sm">{prompt}</p>
          
          <textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={examples.join(' / ')}
            className="w-full h-32 p-3 bg-zinc-800/50 border border-zinc-700/50 rounded-lg text-white placeholder:text-zinc-600 resize-none focus:outline-none focus:border-opacity-50"
            style={{ borderColor: `${color}30` }}
            autoFocus
          />
          
          {/* Example starters */}
          <div className="flex flex-wrap gap-2">
            {examples.map((ex, i) => (
              <button
                key={i}
                onClick={() => onChange(value ? `${value}\n${ex}` : ex)}
                className="px-2 py-1 text-xs rounded-full bg-white/5 text-white/50 hover:text-white hover:bg-white/10 transition-colors"
              >
                {ex}
              </button>
            ))}
          </div>
        </div>
        
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} className="text-white/60">
            Cancel
          </Button>
          <Button 
            onClick={() => { onSave(value); onClose(); }}
            style={{ backgroundColor: color }}
            className="text-white"
          >
            <CheckIcon size={14} color="#FFFFFF" className="mr-1" />
            Add to Canvas
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export { OFNR_QUADRANTS };
export default NVCCanvasOverlay;
